import React from 'react'
import Footer from '../Footer/Footer'
import Header from '../HeaderNavbar/Header'
import HeaderShort from '../HeaderShort/HeaderShort'
import { useStaticQuery, graphql, Script } from 'gatsby'
import { useLocation } from '@reach/router'
import { defaultDataLayer, notificationBarPages } from '../../constants/content'
import { GTM_TOKEN } from '../../constants/env'
import { useRouteChangeAnalytics } from '../../hooks/useRouteChangeAnalytics'
import { useSmartSignalsContext } from '../../context/BotdContext'

interface LayoutTemplateProps {
  children: React.ReactNode
  darkVariant?: boolean
  isCmsPreview?: boolean
  headerShortVersion?: boolean
  showContactSalesButton?: boolean
  footerShortVersion?: boolean
  isHomepage?: boolean
  demoMenuEmbeddedExperiment?: boolean
  footerBackground?: boolean
}

export function NormalLayout({
  children,
  darkVariant,
  headerShortVersion,
  footerShortVersion,
  showContactSalesButton,
  isHomepage,
  demoMenuEmbeddedExperiment,
  footerBackground,
}: LayoutTemplateProps) {
  const { pathname } = useLocation()

  const { visitorData } = useSmartSignalsContext()

  // Also exclude VM visitors
  const isNotBot =
    visitorData &&
    visitorData.products.botd.data.bot.result !== 'bad' &&
    !visitorData.products.virtualMachine.data.result

  const data = useStaticQuery(graphql`
    query NotificationQuery {
      notificationBarYaml {
        arrowText
        barBody
        showNotBar
        url
        selectPages
        showNotificationBell
        showEmoji
        emoji
        theme
      }
    }
  `)

  useRouteChangeAnalytics()

  const showNotificationBar =
    data.notificationBarYaml.showNotBar && isPathInSelectedPages(pathname, data.notificationBarYaml.selectPages)

  const shouldLoadVwoScript =
    pathname.startsWith('/contact-sales') ||
    pathname.startsWith('/new-account-fraud-prevention') ||
    pathname.startsWith('/products/account-sharing-prevention') ||
    pathname.startsWith('/account-takeover') ||
    pathname.startsWith('/payment-fraud')

  return (
    // add rel=preconnect resources to gatsby-ssr.js
    <>
      {/* Do not enable GTM for bots */}
      {isNotBot && (
        <>
          <Script id='gtm-dataLayer'>{`dataLayer = ${JSON.stringify(defaultDataLayer)};`}</Script>
          <Script>
            {/* Define dataLayer and the gtag function.*/}
            {`function gtag(){
                dataLayer.push(arguments);
              }
              gtag('consent', 'default', {
                'ad_storage': '',
                'ad_user_data': '',
                'ad_personalization': '',
                'analytics_storage': 'granted'
              });
            `}
          </Script>
          <Script id='gtm-init'>
            {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f); })(window,document,'script','dataLayer','${GTM_TOKEN}');`}
          </Script>
        </>
      )}
      {shouldLoadVwoScript && (
        <Script
          id='vwo-script'
          strategy='post-hydrate'
          dangerouslySetInnerHTML={{
            __html: `
            window._vwo_code || (function() {
            var account_id=791512,
            version=2.0,
            settings_tolerance=2000,
            hide_element='body',
            hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',

            f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=this.getSettings(),n=d.createElement('script'),i=this;if(t){n.textContent=t;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);i.load(e)}}else{n.fetchPriority='high';n.src=e;n.type='text/javascript';n.onerror=function(){_vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(n)}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t=d.currentScript,n=d.createElement('style'),i=this.hide_element(),r=t&&!t.async&&i?i+this.hide_element_style():'',c=d.getElementsByTagName('head')[0];n.setAttribute('id','_vis_opt_path_hides');v&&n.setAttribute('nonce',v.nonce);n.setAttribute('type','text/css');if(n.styleSheet)n.styleSheet.cssText=r;else n.appendChild(d.createTextNode(r));c.appendChild(n);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version)}};w._vwo_code=code;code.init();})();
            `,
          }}
        />
      )}

      {headerShortVersion ? (
        <HeaderShort showContactSalesButton={showContactSalesButton} isHomepage={isHomepage} />
      ) : (
        <Header
          notificationBar={showNotificationBar && data.notificationBarYaml}
          darkMode={darkVariant}
          isHomepage={isHomepage}
          demoMenuEmbeddedExperiment={demoMenuEmbeddedExperiment}
        />
      )}
      {children}
      <Footer shortVersion={footerShortVersion} footerBackground={footerBackground} />
    </>
  )
}

export function PreviewLayout({ children, darkVariant }: LayoutTemplateProps) {
  return (
    <>
      <Header darkMode={darkVariant} />
      {children}
      <Footer />
    </>
  )
}

// We need PreviewLayout to not use static GraphQL queries in order use it in CMS preview (it runs it in browser directly)
export function LayoutTemplate(props: LayoutTemplateProps) {
  return props.isCmsPreview ? <PreviewLayout {...props} /> : <NormalLayout {...props} />
}

// Function to normalize the pathname.
function normalizePath(path: string): string {
  return `/${path.replace(/^\/+|\/+$/g, '')}/`
}

function isPathInSelectedPages(pathname: string, selectedPages: string[]): boolean {
  const normalizedPathname = normalizePath(pathname)

  for (const pageName of selectedPages) {
    if (pageName === 'All') return true

    const page = notificationBarPages.find((p) => p.name === pageName)
    if (page) {
      for (const pagePath of page.pages) {
        const normalizedPagePath = normalizePath(pagePath)

        // The condition below considers Homepage ('/') and its subsequent paths as part of 'Homepage'
        if (
          normalizedPathname === normalizedPagePath ||
          (normalizedPagePath !== '//' && normalizedPathname.startsWith(normalizedPagePath))
        ) {
          return true
        }
      }
    }
  }
  return false
}
